import ReactDOM from 'react-dom'
// Loading react-dom/client is what populates the internals slot below (Next.js
// already does this to hydrate, but importing it here makes that not implicit).
import 'react-dom/client'

/**
 * React 19 removed the public `ReactDOM.findDOMNode`, but some dependencies
 * we still use call it directly without passing a `nodeRef`, so they throw
 * `TypeError: ReactDOM.findDOMNode is not a function` as soon as that code
 * path runs — confirmed for @wartek-id/banner (on mount) and react-draggable,
 * which react-grid-layout uses for its drag handles (on first drag).
 *
 * React still ships the real implementation internally — react-dom/client
 * wires it onto ReactDOM's private internals object — so we restore the
 * public name from there instead of reimplementing fiber-walking ourselves.
 * If that private object is ever renamed or removed in a future react-dom
 * version, we fall back to a manual walk via the documented-by-React-source
 * `_reactInternals` fiber pointer. Both paths are verified against a real
 * class Transition instance and against @wartek-id/banner directly.
 *
 * Delete this once every dependency above stops calling findDOMNode.
 */
function manualFindDOMNode(component: unknown): Node | null {
  if (component == null) return null
  if (
    typeof component === 'object' &&
    typeof (component as Node).nodeType === 'number'
  ) {
    return component as Node
  }

  let fiber = (component as { _reactInternals?: unknown })._reactInternals as
    | { stateNode?: unknown; child?: unknown }
    | undefined
  if (fiber == null) return null

  let node: { stateNode?: unknown; child?: unknown } | undefined = fiber
  while (node) {
    const stateNode = node.stateNode as Node | undefined
    if (stateNode && stateNode.nodeType === 1) {
      return stateNode
    }
    node = node.child as typeof node
  }
  return null
}

export function installFindDOMNodePolyfill(): void {
  if (typeof ReactDOM.findDOMNode === 'function') return

  const internals = (
    ReactDOM as unknown as {
      __DOM_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE?: {
        findDOMNode?: unknown
      }
    }
  ).__DOM_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE

  const realFindDOMNode = internals?.findDOMNode
  ;(ReactDOM as unknown as { findDOMNode: unknown }).findDOMNode =
    typeof realFindDOMNode === 'function' ? realFindDOMNode : manualFindDOMNode
}

installFindDOMNodePolyfill()
